/**
 * finalizeMultiEditedCell.ts - Commit a multi-window cell edit.
 *
 * When a cell is edited in the multi-window editor, the committed value can come from either the
 * typed formula text or a structured `typedValue` (e.g. a chip/storable). The pure
 * {@link finalizeMultiEditedCell} encodes the precedence between those two sources;
 * {@link finalizeMultiEdit} wraps it with the undo checkpoint, persistence, and analytics.
 */

import produce from 'immer';
import isEqual from 'react-fast-compare';

import {assert} from 'Asserts';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {type Table} from 'notebook/tables/constants/TableConstants';
import {type TableIndex} from 'notebook/tables/constants/TableSelectionConstants';
import {type TableMutationResult} from 'notebook/tables/excess/ops/TableOpConstants';
import {persistTable, type TableMutationParams} from 'notebook/tables/store/logic/mutations/mutationParams';
import {saveSelection} from 'notebook/tables/store/logic/mutations/selection';
import TableUtil from 'notebook/tables/TableUtil';
import {isTypedValueEmpty} from 'notebook/tables/typed-values/DataTypes';
import {type TypedValue} from 'notebook/tables/typed-values/TypedValueConstants';
import {trackContainerValidityForTableEvents} from 'samples/containers/ValidContentsUtils';

export interface FinalizeMultiEditOptions {
  /**
   * Runs after the selection is checkpointed for undo and before the edit is persisted. Only runs
   * when the cell changed.
   */
  beforePersist?: () => void;
}

/**
 * Commit the multi-window edit of the cell at `index`: checkpoint the selection for undo, persist
 * the cell with an `update-cells` op, and track the containers removed from the old cell. No-op
 * when neither the text nor the typed value changed.
 */
export function finalizeMultiEdit(
  params: TableMutationParams,
  index: TableIndex,
  formula: string,
  typedValue: TypedValue | null,
  {beforePersist}: FinalizeMultiEditOptions = {}
): void {
  assert(params.getTableModel().selection.hasRealSelection, 'Expected to have a real selection');
  const {table, fileId} = params.getTableProps();

  const result = finalizeMultiEditedCell(table, index, formula, typedValue);
  if (result == null) {
    return;
  }

  saveSelection(params);
  beforePersist?.();
  persistTable(params, result);
  trackContainerValidityForTableEvents(fileId, table.apiId!, [
    {
      event: ELN_ANALYTICS_ACTIONS.REMOVED_CONTAINERS_FROM_STRUCTURED_TABLE,
      cells: result.affectedCells.flatMap(({oldCell}) => (oldCell != null ? [oldCell] : [])),
    },
  ]);
}

/**
 * Compute the result of finalizing a multi-window cell edit.
 *
 * Precedence rules:
 *   - A non-empty `typedValue` wins: it is stored and the cell text is cleared.
 *   - Otherwise the `typedValue` is cleared (set to null) and the formula text is stored.
 *
 * @param table - The current table
 * @param index - The [row, column] index of the edited cell
 * @param formula - The text typed into the cell editor
 * @param typedValue - The structured value for the cell, or null if there is none
 * @returns The mutation result, or `null` if the cell is unchanged (no commit needed)
 */
export function finalizeMultiEditedCell(
  table: Table,
  index: TableIndex,
  formula: string,
  typedValue: TypedValue | null
): TableMutationResult | null {
  const [i, j] = index;
  const oldCell = table.table[i][j];
  const oldTypedValue = oldCell.typedValue ?? null;

  // Skip updating the table if the cell hasn't changed.
  if (formula === oldCell.text && isEqual(typedValue, oldTypedValue)) {
    return null;
  }

  const editedCell = TableUtil.makeCell(formula);
  if (typedValue != null && !isTypedValueEmpty(typedValue)) {
    editedCell.typedValue = typedValue;
    editedCell.text = '';
  } else {
    editedCell.typedValue = null;
  }

  const newCell = {...oldCell, ...editedCell};
  return {
    table: produce(table, (draftTable) => {
      draftTable.table[i][j] = newCell;
    }),
    tableOp: {type: 'update-cells', cells: [{row: i, column: j}]},
    affectedCells: [{row: i, column: j, oldCell, newCell}],
  };
}
